import React, { type FunctionComponent, useCallback, useEffect, useState } from 'react'
import { CheckboxWithLabel, spacing } from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import {
  type CollectionType,
  type RetailerCollectionStoreConfiguration,
} from 'domains/retailer-collections/components/form/utils/types'
import { RetailerCollectionSurface } from 'domains/retailer-collections/utils/types'

interface RetailerCollectionsFormSurfaceSelectorProps {
  collectionType: CollectionType
  options: RetailerCollectionStoreConfiguration[]
  parentOptions: RetailerCollectionStoreConfiguration[]
  value: RetailerCollectionStoreConfiguration[]
  onChange: (data: RetailerCollectionStoreConfiguration[]) => void
}

const CheckboxWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: spacing.X16,
})

const CheckboxUrl = styled.div({
  fontSize: '13px',
  marginLeft: spacing.X24,
})

export const RetailerCollectionsFormSurfaceSelector: FunctionComponent<
  RetailerCollectionsFormSurfaceSelectorProps
> = ({ onChange, options, parentOptions, value, collectionType }) => {
  const [state, setState] = useState<RetailerCollectionStoreConfiguration[]>(value)

  const onLocalChange = useCallback(
    (evt: React.FormEvent<HTMLInputElement>) => {
      let nextState = [...state]
      const currentValue = evt.currentTarget.value
      const isChecked = evt.currentTarget.checked

      const currentStoreConfig = options.find(option => option.id === currentValue)
      if (!isChecked) {
        nextState = state.filter(value => value.id !== currentStoreConfig.id)
      } else {
        nextState.push({ ...currentStoreConfig })
      }

      setState(nextState)
    },
    [state, options]
  )

  useEffect(() => {
    onChange?.(state)
  }, [onChange, state])

  const parentOptionIds = parentOptions?.map(e => e.id)

  return (
    <div css={{ display: 'flex', flexDirection: 'column', paddingTop: spacing.X12 }}>
      {options?.map(option => {
        const available = !parentOptionIds || parentOptionIds.includes(option.id)
        // cannot disable Marketplace for IC Collections
        const icMarketplace =
          collectionType === 'instacart' && option.surface === RetailerCollectionSurface.Marketplace

        return (
          <CheckboxWrapper key={option.id}>
            <CheckboxWithLabel
              id={`surface-${option.id}`}
              label={option.name}
              value={option.id}
              onChange={onLocalChange}
              checked={state.map(value => value.id).includes(option.id) && available}
              disabled={!available || icMarketplace}
            />
            <CheckboxUrl>{option.url}</CheckboxUrl>
          </CheckboxWrapper>
        )
      })}
    </div>
  )
}
